import React, { useEffect, useState } from "react";
import { FileSignature, CheckCircle2, Clock, ChevronRight, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHead, Loader, EmptyState } from "../components/PageHead";
import { Badge } from "../components/Badges";
import { fmtDate } from "../lib/format";
import api, { apiErr, API } from "../lib/api";
import { toast } from "sonner";
import { FormFiller } from "../components/FormFiller";

export default function PortalForms() {
  const [rows, setRows] = useState(null);
  const [filling, setFilling] = useState(null); // form to fill
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/portal/forms");
      setRows(r.data);
    } catch (e) { toast.error(apiErr(e)); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      <PageHead crumb="My Account" title="Forms to Sign"
        sub="Required forms — submission signifies acceptance and lets us proceed with your calibration." />

      {rows === null && <Loader />}
      {rows && rows.length === 0 && (
        <EmptyState icon={FileSignature}>No forms have been published yet.</EmptyState>
      )}

      {rows && rows.length > 0 && (
        <div style={{ display: "grid", gap: 12 }}>
          {rows.map((f) => {
            const sub = f.submission;
            const completed = f.completed;
            return (
              <div key={f.id} className="card pad" data-testid={`portal-form-${f.code}`}
                style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <div style={{ width: 44, height: 44, background: completed ? "#dff6e3" : "var(--card-2)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: completed ? "#1d6a3a" : "var(--gold-deep)" }}>
                  {completed ? <CheckCircle2 size={24} /> : <FileSignature size={22} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className="t-strong" style={{ fontSize: 15 }}>{f.title}</span>
                    <span className="s t-mut mono" style={{ fontSize: 11 }}>{f.code} · rev {f.version}</span>
                    {completed
                      ? <Badge label="Signed" cls="b-green" />
                      : <Badge label="Pending" cls="b-amber" />}
                  </div>
                  {f.description && <div className="s t-mut" style={{ marginTop: 3 }}>{f.description}</div>}
                  {sub && (
                    <div className="s t-mut" style={{ marginTop: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Clock size={12} /> Signed by {sub.signature_name} on {fmtDate(sub.submitted_at)}
                    </div>
                  )}
                </div>
                <button
                  className={`btn ${completed ? "ghost" : "gold"}`}
                  onClick={() => setFilling(f)}
                  data-testid={`portal-form-open-${f.code}`}
                >
                  {completed ? "View / Re-sign" : "Open form"} <ChevronRight size={14} />
                </button>
                {completed && sub?.has_pdf && (
                  <button className="btn ghost" data-testid={`portal-form-pdf-${f.code}`}
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("pte_token");
                        const resp = await fetch(`${API}/form-submissions/${sub.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
                        if (!resp.ok) throw new Error("PDF unavailable");
                        const blob = await resp.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a"); a.href = url; a.download = `${f.code}-signed.pdf`; a.click();
                        window.URL.revokeObjectURL(url);
                      } catch (e) { toast.error(e.message); }
                    }}>
                    <Download size={13} /> PDF
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {filling && (
        <FormFiller
          form={filling}
          existingSubmission={filling.submission}
          onClose={() => setFilling(null)}
          onSubmitted={() => { setFilling(null); load(); navigate("/app/portal-forms"); toast.success("Thank you — your form has been submitted."); }}
        />
      )}
    </>
  );
}
